/**
 * 屏幕 0：主屏（雪屏 + 数字雨 + 输入框）
 */

import { createSnow } from "../snow";
import { createDrift, type DriftController } from "../drift";
import { isValidName } from "../charAnalyzer";
import { getNamePool, addName } from "../nameHistory";
import { analyze } from "../charAnalyzer";
import { generateMonsters } from "../api";
import { addMonsters, getCodexCount } from "../codex";
import { getZoo } from "../zoo";
import seedPool from "../../data/nameSeedPool.json";
import type { AnyGenerateResponse, CharAnalysis, MonsterDesc } from "../../shared/types";

export interface Screen0Deps {
  root: HTMLElement;
  fontFamily: string;
  onMonsters: (name: string, analysis: CharAnalysis, monsters: MonsterDesc[]) => void;
  onError: (msg: string) => void;
  onGoZoo?: () => void;
  onGoBattle?: () => void;
}

export interface Screen0Controller {
  destroy: () => void;
  setBusy: (busy: boolean) => void;
}

export function createScreen0(deps: Screen0Deps): Screen0Controller {
  const { root, fontFamily, onMonsters, onError, onGoZoo, onGoBattle } = deps;

  // 雪屏
  const destroySnow = createSnow(root);

  // 数字雨
  const seedNames = (seedPool as { names: string[] }).names;
  const namePool = getNamePool(seedNames);
  const drift: DriftController = createDrift(root, fontFamily, {
    nameCount: 12,
    blockCount: 18,
    speed: 60,
    namePool: namePool.length > 0 ? namePool : seedNames.slice(0, 30),
  });

  // 屏幕容器
  const screen = document.createElement("div");
  screen.className = "screen-0";

  // 输入框
  const inputWrap = document.createElement("div");
  inputWrap.className = "input-wrap";

  const promptEl = document.createElement("span");
  promptEl.className = "prompt";
  promptEl.textContent = "> 输入名字";

  const input = document.createElement("input");
  input.type = "text";
  input.maxLength = 8;
  input.placeholder = "";
  input.spellcheck = false;
  input.autocomplete = "off";
  input.autocapitalize = "off";

  const errHint = document.createElement("div");
  errHint.className = "err-hint";
  errHint.style.display = "none";

  const codexHint = document.createElement("div");
  codexHint.className = "codex-count";
  const initialCount = getCodexCount();
  const zooCount = getZoo().length;
  codexHint.innerHTML = `小记：已解锁 <b>${initialCount}</b> 只 · 圈养 <b>${zooCount}</b>/4`;

  inputWrap.appendChild(promptEl);
  inputWrap.appendChild(input);
  inputWrap.appendChild(errHint);

  // 把输入框 + 按钮包成 center-stack，绝对居中
  const centerStack = document.createElement("div");
  centerStack.className = "screen0-center";
  centerStack.appendChild(inputWrap);

  if (onGoZoo) {
    const zooBtn = document.createElement("button");
    zooBtn.className = "nav-btn screen0-nav-btn";
    zooBtn.innerHTML = `◆ 捕获图鉴`;
    zooBtn.addEventListener("click", () => onGoZoo());
    centerStack.appendChild(zooBtn);
  }
  if (onGoBattle) {
    const battleBtn = document.createElement("button");
    battleBtn.className = "nav-btn screen0-nav-btn";
    battleBtn.innerHTML = `⚔ 系统对战`;
    battleBtn.addEventListener("click", () => onGoBattle());
    centerStack.appendChild(battleBtn);
  }
  screen.appendChild(centerStack);

  // 累计计数放在屏幕底部（不参与居中）
  screen.appendChild(codexHint);

  root.appendChild(screen);

  // 自动聚焦
  setTimeout(() => input.focus(), 100);

  let busy = false;
  const setBusy = (b: boolean) => {
    busy = b;
    input.disabled = b;
    if (b) {
      errHint.style.display = "none";
      inputWrap.classList.remove("error");
    }
  };

  const showError = (msg: string) => {
    inputWrap.classList.add("error");
    errHint.textContent = msg;
    errHint.style.display = "block";
  };

  const handleSubmit = async () => {
    if (busy) return;
    const name = input.value.trim();
    if (!isValidName(name)) {
      showError("请输入 1-8 个中文字");
      return;
    }
    setBusy(true);
    // 拆字
    const analysis = analyze(name);
    // 入池
    addName(name);
    drift.appendName(name);
    // 调 API
    const result: AnyGenerateResponse = await generateMonsters({ name, charAnalysis: analysis });
    if (!result.ok) {
      onError(result.error);
      setBusy(false);
      return;
    }
    // 累计图鉴
    addMonsters(result.monsters);
    codexHint.innerHTML = `小记：已解锁 <b>${getCodexCount()}</b> 只`;
    onMonsters(name, analysis, result.monsters);
  };

  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  });
  input.addEventListener("input", () => {
    inputWrap.classList.remove("error");
    errHint.style.display = "none";
  });

  // 销毁
  const destroy = () => {
    destroySnow();
    drift.destroy();
    screen.remove();
  };

  return { destroy, setBusy };
}
